import React from "react";
import { Typography, Card, Progress } from "antd";

const { Title, Text } = Typography;

/**
 * Read-only Step 2 progress panel. Nexxen is the only destination, so there
 * is nothing for the user to choose here — this purely reflects whatever
 * `GuidedWorkflowSteps` learned from the last publish-status poll (or from
 * the activation call itself).
 *
 * Pulled out as a small presentational component so it can be shallow-tested
 * directly: the parent's data flow (auto-fire effect + polling) only runs
 * under a real DOM render, which shallow rendering doesn't provide.
 */
const PublishStatusPanel = ({
  audienceName,
  recordCount = 0,
  dataTypeLabel = "Emails",
  publishStatus,
  uploadErrorMessage,
  queryErrorMessage,
  hasFailed,
  hasCompleted,
}) => {
  // Bull evicts completed jobs, so `state: "unknown"` on its own doesn't
  // mean anything went wrong — it means the background job can no longer be
  // observed. If that happens while the audience is still mid-publish (not
  // failed, not completed), the honest answer is "we lost track of this",
  // not an error and not a spinner that never resolves. Derived from props
  // already passed in by both callers (GuidedWorkflowSteps and
  // FirstPartyDataDetailController) — no new prop required.
  const isEvicted = publishStatus?.state === "unknown" && !hasFailed && !hasCompleted;

  let statusText;
  if (uploadErrorMessage) {
    statusText = `${uploadErrorMessage} You can retry from the audience list.`;
  } else if (publishStatus?.state === "failed") {
    statusText =
      publishStatus?.failedReason ||
      "Upload failed. You can retry from the audience list.";
  } else if (queryErrorMessage) {
    statusText = queryErrorMessage;
  } else if (hasCompleted) {
    statusText =
      "Upload complete. Waiting for Nexxen to activate your audience.";
  } else if (isEvicted) {
    statusText =
      "We can no longer track this publish job's progress. It may have finished without reporting back — check the audience list, or re-publish this audience if it doesn't complete.";
  } else {
    statusText = "Uploading audience partitions to the Nexxen SFTP…";
  }

  return (
    <div>
      <Title level={4}>
        {isEvicted ? "Publish Status Unknown" : "Publishing to Nexxen"}
      </Title>
      <Text style={{ display: "block", marginBottom: "24px" }}>
        {isEvicted
          ? "We're no longer able to observe this publish job in the background. It may have completed successfully, or it may need to be retried."
          : "We're creating your Nexxen taxonomy entry and uploading your audience file to the Nexxen SFTP. You can minimize or close this window — the upload continues in the background, and you'll see the result the next time you open this audience."}
      </Text>

      {(audienceName || recordCount > 0) && (
        <Card
          size="small"
          style={{
            marginBottom: "20px",
            backgroundColor: "#f8f9fa",
            border: "1px solid #e9ecef"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <Text strong style={{ color: "#495057" }}>Audience: </Text>
              <Text style={{ color: "#6c757d" }}>
                {audienceName || "Untitled Audience"}
              </Text>
            </div>
            <div>
              <Text strong style={{ color: "#495057" }}>{dataTypeLabel} Count: </Text>
              <Text style={{ color: "#6c757d" }}>{recordCount.toLocaleString()}</Text>
            </div>
          </div>
        </Card>
      )}

      <Progress
        percent={publishStatus?.progress ?? 0}
        status={hasFailed ? "exception" : isEvicted ? "normal" : "active"}
      />

      <Text
        type={hasFailed ? "danger" : "secondary"}
        style={{ display: "block", marginTop: "12px" }}
      >
        {statusText}
      </Text>
    </div>
  );
};

export default PublishStatusPanel;
